import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Admin } from './pages/Admin';
import Hero from './components/Hero';
import Skills from './components/Skills';
import Projects from './components/Projects';
import Experience from './components/Experience';
import { StepNavigation } from './components/StepNavigation';
import { motion } from 'framer-motion';

export function Portfolio() {
  const [dark, setDark] = useState(true);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
  
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
      setDark(true);
    } else if (savedTheme === 'light') {
      document.documentElement.classList.remove('dark');
      setDark(false);
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        document.documentElement.classList.add('dark');
        setDark(true);
      } else {
        document.documentElement.classList.remove('dark');
        setDark(false);
      }
    }
  }, []);
  
  const toggleTheme = () => {
    const newDark = !dark;
    setDark(newDark);
  
    if (newDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const heroRef = useRef<HTMLDivElement>(null);
  const skillsRef = useRef<HTMLDivElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);
  const experienceRef = useRef<HTMLDivElement>(null);
  const [currentSection, setCurrentSection] = useState(0);
  const scrollTo = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const sectionRefs = [heroRef, skillsRef, projectsRef, experienceRef];
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
  
    sectionRefs.forEach((ref, index) => {
      if (!ref.current) return;
  
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setCurrentSection(index);
          }
        },
        {
          threshold: 0.6, // 60% visible
        }
      );
  
      observer.observe(ref.current);
      observers.push(observer);
    });
  
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);
  
  
  useVh();

  return (
    <div id='main'   
    style={{ height: "calc(var(--vh) * 100)" }}
    className="
      overflow-y-scroll
      scroll-smooth
      snap-y snap-mandatory
      transition-colors duration-300
      text-gray-900 dark:text-white font-sans
      pt-[env(safe-area-inset-top)]
      pb-[env(safe-area-inset-bottom)]
    "
    >
      <StepNavigation
        sections={['Hero', 'Skills', 'Projects', 'Experience']}
        currentSection={currentSection}
        onStepClick={(i) => scrollTo(sectionRefs[i])}
      />
      {/* Hero Section */}
      <div ref={heroRef}>
        <Hero toggleTheme={toggleTheme} dark={dark} scrollTo={scrollTo} heroRef={heroRef} skillRef={skillsRef} projectsRef={projectsRef} experienceRef={experienceRef} />
      </div>

      {/* Skills */}
      <div ref={skillsRef} className="">
        <Skills scrollTo={scrollTo} projectsRef={projectsRef} />
      </div>
   
      {/* Projects */}
      <div ref={projectsRef}>
        <Projects scrollTo={scrollTo} experienceRef={experienceRef} />
      </div>

      {/* Experience */}
      <div ref={experienceRef}>
        <Experience />
      </div>

      {/* Footer */}
      <footer className="md:snap-start max-w-6xl mx-auto w-full text-gray-900 dark:text-white py-12">
        <div className="px-4 md:px-0 flex flex-col md:flex-row items-center justify-between gap-8">    
          {/* Texte animé */}
          <motion.h5
            className="text-2xl md:text-3xl ralewaytitle italic text-center md:text-left"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Creative Developer • Shaping the future of the web
          </motion.h5>
        </div>
        {/* Ligne + copyright */}
        <div className='flex justify-center md:justify-between'>
          <div className="pt-6 text-xs">
            © {new Date().getFullYear()} Florian GANDERATZ — All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Portfolio />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

function useVh() {
  useEffect(() => {
    function setVh() {
      // 1vh = 1% de la hauteur visible
      document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
    }
    setVh();
    window.addEventListener("resize", setVh);
    return () => window.removeEventListener("resize", setVh);
  }, []);
}

export default App;